import { alpha, createTheme, type Palette, type Theme } from '@mui/material/styles';

export type ColorScheme = 'light' | 'dark';

interface PaletteColorStates {
    hover: string;
    selected: string;
    focus: string;
    focusVisible: string;
    outlinedBorder: string;
}

export type PaletteColorStateKey = keyof PaletteColorStates;

// Resolves to the palette keys that actually have _states (primary, secondary, error, etc.)
// by filtering out non-color entries like mode, background, text, action.
type StatefulPaletteKey = {
    [K in keyof Palette]: Palette[K] extends { _states: PaletteColorStates } ? K : never;
}[keyof Palette];

export const stateColor = (t: Theme, key: PaletteColorStateKey, palette: StatefulPaletteKey = 'primary'): string =>
    t.palette[palette]._states[key];

declare module '@mui/material/styles' {
    interface PaletteColor {
        _states: PaletteColorStates;
    }
    interface SimplePaletteColorOptions {
        _states?: PaletteColorStates;
    }
    interface Palette {
        filledSurfaceOverlay: string;
        neutralSurface: string;
    }
    interface PaletteOptions {
        filledSurfaceOverlay?: string;
        neutralSurface?: string;
    }
}

// Derives interactive state colors from a palette's main color.
// Mirrors the _states tokens in the Figma variable exports (see design/figma-variable-exports/).
// Dark mode doubles hover/selected opacity to stay visible on dark backgrounds.
function colorStates(main: string, dark: boolean): PaletteColorStates {
    const base = dark ? 2 : 1;
    return {
        hover: alpha(main, 0.04 * base),
        selected: alpha(main, 0.08 * base),
        focus: alpha(main, 0.12),
        focusVisible: alpha(main, 0.3),
        outlinedBorder: alpha(main, 0.5),
    };
}

// `light` is never referenced directly: MUI derives the standard-Alert tints from it
// (`palette.Alert.<severity>Color` / `<severity>StandardBg`), and without it those tints are
// derived from `main` and drift away from the values the designs are drawn with.
export const theme = createTheme({
    cssVariables: {
        colorSchemeSelector: 'data-color-scheme',
    },
    defaultColorScheme: 'light',
    colorSchemes: {
        light: {
            palette: {
                primary: {
                    main: '#197797',
                    dark: '#005570',
                    light: '#60A1BD',
                    contrastText: '#FFFFFF',
                    _states: colorStates('#197797', false),
                },
                secondary: {
                    main: '#70BE3B',
                    dark: '#4D9A2A',
                    light: '#9CD178',
                    contrastText: '#FFFFFF',
                    _states: colorStates('#70BE3B', false),
                },
                error: {
                    main: '#D32F2F',
                    dark: '#C62828',
                    contrastText: '#FFFFFF',
                    _states: colorStates('#D32F2F', false),
                },
                warning: {
                    main: '#F9A825',
                    dark: '#F57F17',
                    light: '#FFEB3B',
                    contrastText: '#FFFFFF',
                    _states: colorStates('#F9A825', false),
                },
                info: {
                    main: '#0288D1',
                    dark: '#01579B',
                    light: '#03A9F4',
                    contrastText: '#FFFFFF',
                    _states: colorStates('#0288D1', false),
                },
                success: {
                    main: '#2E7D32',
                    dark: '#1B5E20',
                    light: '#4CAF50',
                    contrastText: '#FFFFFF',
                    _states: colorStates('#2E7D32', false),
                },
                background: {
                    default: '#FAFAFA',
                    paper: '#FFFFFF',
                },
                text: {
                    primary: 'rgba(0, 0, 0, 0.87)',
                },
                action: {
                    active: 'rgba(0, 0, 0, 0.54)',
                    disabled: 'rgba(0, 0, 0, 0.26)',
                    hover: 'rgba(0, 0, 0, 0.04)',
                    disabledBackground: 'rgba(0, 0, 0, 0.12)',
                },
                filledSurfaceOverlay: alpha('#FFFFFF', 0.12),
                neutralSurface: 'rgba(0, 0, 0, 0.04)',
            },
        },
        dark: {
            palette: {
                primary: {
                    main: '#86BDD7',
                    dark: '#428CAA',
                    light: '#CCF3FF',
                    contrastText: 'rgba(0, 0, 0, 0.87)',
                    _states: colorStates('#86BDD7', true),
                },
                secondary: {
                    main: '#B8DE9F',
                    dark: '#86C859',
                    light: '#EEF7E8',
                    contrastText: 'rgba(0, 0, 0, 0.87)',
                    _states: colorStates('#B8DE9F', true),
                },
                error: {
                    main: '#F44336',
                    dark: '#D32F2F',
                    contrastText: '#FFFFFF',
                    _states: colorStates('#F44336', true),
                },
                warning: {
                    main: '#FFEE58',
                    dark: '#FBC02D',
                    light: '#FFF176',
                    contrastText: 'rgba(0, 0, 0, 0.87)',
                    _states: colorStates('#FFEE58', true),
                },
                info: {
                    main: '#29B6F6',
                    dark: '#0288D1',
                    contrastText: 'rgba(0, 0, 0, 0.87)',
                    _states: colorStates('#29B6F6', true),
                },
                success: {
                    main: '#66BB6A',
                    dark: '#388E3C',
                    contrastText: 'rgba(0, 0, 0, 0.87)',
                    _states: colorStates('#66BB6A', true),
                },
                background: {
                    default: '#121212',
                    paper: '#1E1E1E',
                },
                text: {
                    primary: '#FFFFFF',
                },
                action: {
                    active: '#FFFFFF',
                    disabled: 'rgba(255, 255, 255, 0.3)',
                    hover: 'rgba(255, 255, 255, 0.08)',
                    disabledBackground: 'rgba(255, 255, 255, 0.12)',
                },
                filledSurfaceOverlay: alpha('#000000', 0.12),
                neutralSurface: 'rgba(255, 255, 255, 0.08)',
            },
        },
    },
    typography: {
        fontFamily: '"Noto Sans Variable", Helvetica, Arial, sans-serif',
        h1: { fontWeight: 700 },
        h2: { fontWeight: 700 },
        h3: { fontWeight: 700 },
        h4: { fontSize: '2rem', fontWeight: 700 },
        h5: { fontWeight: 700 },
        h6: { fontWeight: 700 },
        subtitle1: { fontWeight: 700 },
        subtitle2: { fontWeight: 700 },
        overline: { fontWeight: 600 },
        caption: { fontWeight: 600 },
    },
    shape: {
        borderRadius: 8,
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                html: {
                    scrollbarGutter: 'stable',
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                standard: ({ theme: t, ownerState }) => ({
                    border: `1px solid ${(t.vars ?? t).palette[ownerState.severity ?? 'success']._states.outlinedBorder}`,
                }),
            },
        },
        MuiModal: {
            defaultProps: {
                disableScrollLock: true,
            },
        },
        MuiPopover: {
            defaultProps: {
                disableScrollLock: true,
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none',
                    },
                },
            },
        },
        // Material Design helper text is regular weight; `typography.caption` stays
        // semibold for chips/overlines so we only relax weight on FormHelperText.
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontWeight: 400,
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    minHeight: 48,
                },
            },
        },
    },
});
